import { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Sidebar from "./components/Sidebar";
import MobileNavbar from "./components/MobileNavbar";
import ProtectedRoute from "./routes/ProtectedRoute";
import { protectedRoutes, publicRoutes } from "./routes/routes";
import NotFoundPage from "./pages/NotFoundPage";
import { useAuth } from "./context/AuthContext";
import MobileHeader from "./components/MobileHeader";
import ComingSoon from "./pages/ComingSoon";
import { membershipAxios } from "./services/membership/MembershipAxios";
import Loader from "./components/common/Loader";
import ButtonWs from "./components/ButtonWs";
import { getSurveyValidateFormSurvey } from "./services/survey/surveyService";
import { getUserSettings } from "./services/settings/userSettings";
import { handleApi } from "./utils/handleApi";
import { decodeTokenService } from "./services/decodeTokenService";
import { Language } from "./utils/dictionary";
import { getGeoLocation } from "./services/settings/geoLocation";
import WelcomeModal from "./components/WelcomeModal";
import TutorialTooltip from "./components/TutorialTooltip";
import { TutorialStep } from "./components/TutorialTooltip";
import { useTutorialContext } from "./context/TutorialContext";
import { TutorialSetup } from "./components/TutorialSetup";

const tutorialSteps: TutorialStep[] = [
  {
    id: "createSalesPage",
    question_text: "Crear página de ventas",
    description: "Inicia dándole clic al botón para empezar a crear tu primera página de ventas de manera guiada.",
    type: "single",
    options: null,
    next_step: "chooseOption",
    required: true,
    is_summary: false,
    survey_id: "default",
    is_first_step: true
  },
  {
    id: "chooseOption",
    question_text: "Elige cómo empezar",
    description: "Tendrás 3 opciones diferentes para cargar tu producto ganador.",
    type: "single",
    options: null,
    next_step: "orderBump",
    required: true,
    is_summary: false,
    survey_id: "default",
    is_first_step: false
  },
  {
    id: "orderBump",
    question_text: "Order Bump",
    description: "Es una oferta rápida en el checkout. Perfecto para artículos pequeños o complementarios.",
    type: "single",
    options: null,
    next_step: "salesAngles",
    required: true,
    is_summary: false,
    survey_id: "default",
    is_first_step: false
  },
  {
    id: "salesAngles",
    question_text: "Ángulos de venta",
    description: "Selecciona el enfoque de marketing ideal para vender tu producto. Identificamos los dolores más importantes de tu usuario final, para generar el contenido persuasivo de toda tu página de ventas y maximizar tus conversiones.",
    type: "single",
    options: null,
    next_step: null,
    required: true,
    is_summary: false,
    survey_id: "default",
    is_first_step: false
  }
];

const AppContent = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const [isHovered, setIsHovered] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  // const [isLoading, setIsLoading] = useState(false);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader />
      </div>
    );
  }

  const verifyMembership = async () => {
    if (
      protectedRoutes.some((route) => location.pathname === route.path) ===
      false
    ) {
      return;
    }

    const skipRoutes = ["/settings", "/user/preferences", "/survey"];
    if (skipRoutes.includes(location.pathname)) {
      return;
    }

    try {
      // setIsLoading(true);
      const membership = await membershipAxios();
      if (location.pathname === "/plans" && membership.credits > 0) {
        navigate("/home");
      }
      if (location.pathname === "/onboarding" && membership.credits < 1) {
        navigate("/plans");
      }
      const redirectedToPreferences = await checkUserPreferences();
      if (redirectedToPreferences) return;

      await checkSurvey();
      // setIsLoading(false);
    } catch (error: any) {
      console.error("Error fetching membership:", error);
      if (error.response?.data.trigger === "NOT_ACTIVE_MEMBERSHIP") {
        navigate("/plans");
      }
      // setIsLoading(false);
    }
  };

  const checkUserPreferences = async (): Promise<boolean> => {
    const { error } = await handleApi(getUserSettings(), false);

    if (!error) return false;

    if (error.response?.status === 404) {
      navigate("/user/preferences");
      return true;
    }

    const errorData = error.response?.data as { message?: string } | undefined;
    toast.error(errorData?.message || "Error loading user settings");
    return false;
  };

  const checkSurvey = async () => {
    const userId = decodeTokenService.getUserIdFromToken();
    if (!userId) return;
    const survey = await getSurveyValidateFormSurvey("default", "user", userId);
    if (survey.data.status !== "success") {
      navigate("/survey");
    }
  };

  useEffect(() => {
    if (isLoading) return;
    verifyMembership();
  }, [location.pathname, isLoading]);

  useEffect(() => {
    if (isLoading) return;
    if (
      isAuthenticated &&
      (location.pathname === "/login" || location.pathname === "/signup")
    ) {
      navigate("/home");
    }
  }, [isAuthenticated, location.pathname, navigate, isLoading]);

  const routes = ["/", "/login", "/signup", "/login/offer", "/survey"];

  return (
    // <SubscriptionProvider>
    <div
      className={`hard-flex min-h-screen bg-[#F5F6FA] font-['Plus_Jakarta_Sans'] ${location.pathname === "/" ? "p-0" : ""
        }`}
    >
      <div className="md:hidden pb-6 mb-6">
        {isAuthenticated && !routes.includes(location.pathname) ? (
          <MobileHeader />
        ) : (
          <></>
        )}
      </div>
      {/* Sidebar for Desktop */}
      {isAuthenticated && !routes.includes(location.pathname) && (
        <div className="hidden md:block">
          <Sidebar />
        </div>
      )}

      {/* Main Content */}
      <div
        className={`flex-1 ${isAuthenticated &&
          !routes.includes(location.pathname) &&
          location.pathname !== "/"
          ? location.pathname === "/website/builder"
            ? isHovered
              ? "md:ml-60 ml-0"
              : "md:ml-16 ml-0"
            : "md:ml-60 ml-0"
          : "ml-0"
          }`}
      >
        <Routes>
          {/* Public Routes */}
          {publicRoutes.map(({ path, element }) => (
            <Route key={path} path={path} element={element} />
          ))}
          {isLoading ? (
            <Route
              path="*"
              element={
                <div className="flex justify-center items-center h-screen">
                  <Loader />
                </div>
              }
            />
          ) : (
            protectedRoutes.map(({ path, element }) => (
              <Route
                key={path}
                path={path}
                element={<ProtectedRoute>{element}</ProtectedRoute>}
              />
            ))
          )}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </div>

      {/* Mobile Navbar */}
      <div className="md:hidden">
        {isAuthenticated && !routes.includes(location.pathname) ? (
          <MobileNavbar />
        ) : (
          <></>
        )}
      </div>
    </div>
    // </SubscriptionProvider>
  );
};

const App = () => {
  const routes = ["/", "/login/offer"];

  const { setLanguage } = useAuth();

  const languageMap: Record<string, Language> = {
    es: Language.SPANISH,
    en: Language.ENGLISH,
    pt: Language.PORTUGUESE,
  };

  const getUserPreferences = async () => {
    const { res, error } = await handleApi(getUserSettings(), false);

    if (error && error.response?.status === 404) {
      try {
        const geoLocation = await getGeoLocation();
        const geoLanguage = geoLocation?.language;
        if (geoLanguage && languageMap[geoLanguage]) {
          setLanguage(languageMap[geoLanguage]);
        } else {
          setLanguage(Language.SPANISH);
        }
      } catch (geoError) {
        console.error("Error getting geo location:", geoError);
        setLanguage(Language.SPANISH);
      }
      return;
    }

    if (error) {
      setLanguage(Language.SPANISH);
      return;
    }

    if (res?.language) {
      const language = languageMap[res.language];
      if (language) {
        setLanguage(language);
      } else {
        setLanguage(Language.SPANISH);
      }
    }
  };

  useEffect(() => {
    getUserPreferences();
  }, []);

  const {
    isWelcomeOpen,
    isTutorialActive,
    currentStep,
    startTutorial,
    nextStep,
    closeTutorial,
    setSteps,
  } = useTutorialContext();

  useEffect(() => {
    setSteps(tutorialSteps);
  }, []);

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Toaster
        position="top-right"
        containerStyle={{
          zIndex: 99999,
        }}
        toastOptions={{
          duration: 2000,
        }}
      />
      <TutorialSetup />

      {/* <WelcomeModal
        isOpen={isWelcomeOpen}
        onStart={startTutorial}
        onClose={closeTutorial}
      />

      <TutorialTooltip
        step={currentStep}
        onNext={nextStep}
        onClose={closeTutorial}
        isActive={isTutorialActive}
      /> */}

      {/* <InitialSurvey /> */}
      {!routes.includes(location.pathname) ? (
        <>
          <div className="hidden md:block">
            <AppContent />
            <ButtonWs />
          </div>
          <div className="md:hidden">
            <ComingSoon />
          </div>
        </>
      ) : (
        <AppContent />
      )}
    </Router>
  );
};

export default App;
